import { format } from 'd3-format';
import { useMemo } from 'react';

import {
  MapCoordsGranularity,
  TRANSITIONS_V2_DATA,
  type TransitionV2DataQuery,
} from '@revelio/data-access';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../filters/hooks/useGeographyMode';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { getTransitionV2Filters } from '../../filters/utils/getTransitionV2Filters';
import { LocationType, type MapPoint } from '../Chart/Map/types';
import { determineLocationType } from '../Chart/Map/utils/location-type-utils';
import type { TransitionMetricValues } from '../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import {
  extractGranularitiesWithIds,
  useCoordinateResolverForMixedGranularities,
} from './utils/coordinate-resolver';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import { filterEntitiesToFirstEntType } from './utils/transition-entity-utils';

type TransitionMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: TransitionMetricValues;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

// TODO: update TRANSITIONS_V2 to return geography breakdown granularities in the response
export const TransitionMapDataProvider = ({
  primaryDimension,
  metric,
  children,
}: TransitionMapDataProviderProps) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const geographyMode = useGeographyMode();
  const baseReq = getTransitionV2Filters(effectiveFilters, primaryDimension);

  const filtered = baseReq ? filterEntitiesToFirstEntType(baseReq) : null;
  const apiReq = filtered
    ? {
        ...filtered,
        ...(geographyMode === 'administrative' && {
          options: { geo_breakdown_state_over_msa: true },
        }),
      }
    : null;

  const [{ data, status }] = useUrqlQueryWithStatus<TransitionV2DataQuery>({
    query: TRANSITIONS_V2_DATA,
    variables: apiReq ? { req: apiReq } : undefined,
    pause: !apiReq,
  });

  // Aggregate by geography breakdown across all selected entities
  const geography = useMemo(() => {
    const entities = data?.transitionsV2?.entities ?? [];
    const totals = new Map<{ label: string; id: number }, number>();

    entities.forEach((ent) => {
      const geo = ent.metrics?.breakdowns?.geography ?? [];
      geo.forEach((g) => {
        const label = g.short_name || g.long_name || '';
        const id = g.id;
        if (id === undefined) return;

        const v =
          metric === 'transitions_inflow'
            ? (g.inflows_count ?? 0)
            : (g.outflows_count ?? 0);

        if (!Number.isFinite(v) || v <= 0) return;

        const current = totals.get({ label, id }) ?? 0;
        totals.set({ label, id }, current + v);
      });
    });

    return Array.from(totals.entries()).map(([{ label, id }, value]) => ({
      id,
      value,
      label,
    }));
  }, [data, metric]);

  const granularitiesWithIds = extractGranularitiesWithIds(geography);

  const { data: coords, loading: coordsLoading } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const points = useMemo((): MapPoint[] => {
    const allPoints: MapPoint[] = [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));

    const formatValue = format('.2s');

    geography.forEach((entity) => {
      if (!entity.label?.trim() || entity.id === 0) return;

      const coord = coordMap.get(entity.id);
      const locationType = determineLocationType(
        entity.label,
        coord?.granularity || MapCoordsGranularity.Msa
      );

      const needsCoords = locationType === LocationType.METRO_AREA;

      if (!coord && needsCoords) {
        return;
      }

      const prefix = metric === 'transitions_inflow' ? 'Inflow:' : 'Outflow:';
      const formattedValue = `${formatValue(entity.value)} employees`;

      allPoints.push({
        id: String(entity.id),
        label: entity.label,
        lat: coord?.lat || 0,
        lon: coord?.lon || 0,
        value: entity.value,
        locationType,
        tooltip: {
          [prefix]: formattedValue,
        },
      });
    });

    return allPoints;
  }, [geography, coords, metric]);

  const finalStatus = coordsLoading
    ? DataProviderQueryStatus.Fetching
    : (status ?? DataProviderQueryStatus.Idle);

  return children({ data: { points }, status: finalStatus });
};
